import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, Badge, Button, Card, EmptyState, Icon, Input, Tabs } from "../../design-system";
import {
  findCourse,
  fmtMinutes,
  completedLessons,
  nextLesson,
  progressOfCourse,
  totalForumThreads,
  totalLessons,
  totalMaterials,
  totalVolumes,
} from "./data";
import { ProgressBar, Stars } from "./atoms";

function FormacionCurso() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const course = findCourse(courseId);
  const onBack = () => navigate("/formacion");

  const initialNext = useMemo(() => (course ? nextLesson(course) : null), [course]);
  const [currentId, setCurrentId] = useState(initialNext?.lesson.id);
  const [tab, setTab] = useState("contenido");
  const [forumThread, setForumThread] = useState(null);
  const [playing, setPlaying] = useState(false);

  const current = useMemo(() => {
    if (!course) return null;
    for (const v of course.volumes) {
      for (const l of v.lessons) if (l.id === currentId) return { volume: v, lesson: l };
    }
    return initialNext;
  }, [currentId, course, initialNext]);

  if (!course) {
    return (
      <div className="kr-page">
        <EmptyState
          icon="BookOpen"
          title="Formación no encontrada"
          description="El curso que buscas no existe o ya no está disponible en el catálogo."
          action={{ label: "Volver al catálogo", icon: "ArrowLeft", onClick: onBack }}
        />
      </div>
    );
  }

  document.title = `${course.title} | Formación · Kronaly`;
  const { volume: currentVol, lesson: currentLesson } = current;

  const progress = progressOfCourse(course);

  // Barra de progreso del reproductor (sólo visual).
  const lessonElapsed = currentLesson.completed ? 1 : 0.34;

  return (
    <div className="kr-page" style={{ gap: 16 }}>
      {/* Volver / estado */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
        <button
          type="button"
          onClick={onBack}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "var(--kr-fg-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "4px 0" }}
        >
          <Icon name="ArrowLeft" size={14} />
          Volver al catálogo
        </button>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {course.mandatory && <Badge tone="warning" dot>Obligatorio</Badge>}
          <Badge tone="brand">{Math.round(progress * 100)}% completado</Badge>
        </div>
      </div>

      {/* Título */}
      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
          {course.category} · {course.type === "video" ? "Vídeo" : "Presentación"} · {fmtMinutes(course.durationMin)}
        </div>
        <h1 className="kr-display" style={{ margin: "4px 0 0", fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)", lineHeight: 1.15 }}>
          {course.title}
        </h1>
      </div>

      {/* Reproductor + temario */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, alignItems: "start" }} data-responsive-grid="2">
        <Player course={course} volume={currentVol} lesson={currentLesson} playing={playing} onTogglePlay={() => setPlaying((p) => !p)} elapsed={lessonElapsed} />
        <ChapterSidebar course={course} currentId={currentId} onSelect={setCurrentId} />
      </div>

      {/* Tabs */}
      <Card padded={false}>
        <div style={{ padding: "10px 16px 0" }}>
          <Tabs
            tabs={[
              { id: "contenido", label: "Descripción" },
              { id: "materiales", label: "Materiales", count: totalMaterials(course) },
              { id: "foro", label: "Foro", count: totalForumThreads(course) },
            ]}
            value={tab}
            onChange={(t) => { setTab(t); setForumThread(null); }}
          />
        </div>

        <div style={{ padding: 20 }}>
          {tab === "contenido" && <DescripcionTab course={course} />}
          {tab === "materiales" && <MaterialesTab course={course} />}
          {tab === "foro" && (
            forumThread
              ? <ForumThread course={course} thread={course.forum.find((q) => q.id === forumThread)} onBack={() => setForumThread(null)} />
              : <ForoList course={course} onOpen={setForumThread} />
          )}
        </div>
      </Card>
    </div>
  );
}

// =====================================================================
//  Reproductor (vídeo o presentación)
// =====================================================================
function Player({ course, volume, lesson, playing, onTogglePlay, elapsed }) {
  const isVideo = course.type === "video";
  const lessonDurStr = lesson.duration;
  const [lh, lm] = lessonDurStr.split(":").map(Number);
  const lessonSec = lh * 60 + lm;
  const playedSec = Math.round(lessonSec * elapsed);
  const playedStr = `${String(Math.floor(playedSec / 60)).padStart(2, "0")}:${String(playedSec % 60).padStart(2, "0")}`;

  return (
    <Card padded={false} style={{ overflow: "hidden", background: "#0f1316" }}>
      <div style={{ position: "relative", aspectRatio: "16 / 9", background: "linear-gradient(135deg, #1a2026, #0f1316)" }}>
        {isVideo ? <VideoStage /> : <SlideStage volume={volume} lesson={lesson} />}

        {/* Overlay play */}
        {!playing && (
          <button
            type="button"
            onClick={onTogglePlay}
            style={{ position: "absolute", inset: 0, background: "rgba(15,19,22,0.32)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <span style={{ width: 76, height: 76, borderRadius: 999, background: "rgba(255,255,255,0.95)", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 30px rgba(0,0,0,0.3)" }}>
              <Icon name="Play" size={28} style={{ color: "var(--kr-fg-strong)", marginLeft: 4 }} />
            </span>
          </button>
        )}

        {/* Overlay superior (título de la lección) */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, padding: 16, background: "linear-gradient(180deg, rgba(0,0,0,0.5), transparent)", color: "#fff", pointerEvents: "none" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.75, fontWeight: 600 }}>{volume.title}</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{lesson.title}</div>
        </div>

        {/* Controles inferiores */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: 16, background: "linear-gradient(0deg, rgba(0,0,0,0.66), transparent)", color: "#fff", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ height: 4, background: "rgba(255,255,255,0.25)", borderRadius: 999, position: "relative", cursor: "pointer" }}>
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${elapsed * 100}%`, background: "var(--kr-action)", borderRadius: 999 }} />
            <div style={{ position: "absolute", left: `${elapsed * 100}%`, top: -3, width: 10, height: 10, borderRadius: 999, background: "#fff", transform: "translateX(-50%)" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="button" onClick={onTogglePlay} style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", padding: 4, display: "inline-flex" }}>
              <Icon name={playing ? "Pause" : "Play"} size={18} style={{ color: "#fff" }} />
            </button>
            <PlayerIconBtn icon="ChevronLeft" title="Lección anterior" />
            <PlayerIconBtn icon="ChevronRight" title="Lección siguiente" />
            <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, opacity: 0.9 }}>
              {playedStr} <span style={{ opacity: 0.55 }}>/ {lessonDurStr}</span>
            </span>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 4, cursor: "pointer" }}>1.0x</span>
            <PlayerIconBtn icon="Captions" title="Subtítulos" />
            <PlayerIconBtn icon="Volume2" title="Volumen" />
            <PlayerIconBtn icon="Settings" title="Ajustes" />
            <PlayerIconBtn icon="Maximize" title="Pantalla completa" />
          </div>
        </div>
      </div>

      {/* Bajo el reproductor: acciones de la lección */}
      <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid var(--kr-neutral-900)", background: "var(--kr-bg-surface)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={course.instructor} size={28} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{course.instructor}</div>
            <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{course.instructorRole}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Button variant="ghost" size="sm" icon="Bookmark">Guardar</Button>
          <Button variant="secondary" size="sm" icon="Check">Marcar lección como vista</Button>
        </div>
      </div>
    </Card>
  );
}

function PlayerIconBtn({ icon, title }) {
  return (
    <button
      type="button"
      title={title}
      style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.85)", cursor: "pointer", width: 28, height: 28, borderRadius: 6, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
    >
      <Icon name={icon} size={15} style={{ color: "rgba(255,255,255,0.92)" }} />
    </button>
  );
}

function VideoStage() {
  return (
    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,#1a2026 0%, #0f1316 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg viewBox="0 0 800 450" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.4 }}>
        <defs>
          <linearGradient id="vg1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#3dc53a" stopOpacity="0.55" />
            <stop offset="1" stopColor="#3dc53a" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="vg2" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#2563eb" stopOpacity="0.3" />
            <stop offset="1" stopColor="#2563eb" stopOpacity="0" />
          </linearGradient>
        </defs>
        <circle cx="640" cy="120" r="200" fill="url(#vg1)" />
        <circle cx="120" cy="380" r="220" fill="url(#vg2)" />
      </svg>
    </div>
  );
}

function SlideStage({ volume, lesson }) {
  return (
    <div style={{ position: "absolute", inset: 24, background: "#fff", borderRadius: 8, padding: "32px 40px", display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 12px 32px rgba(0,0,0,0.35)" }}>
      <div style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: "#888", fontWeight: 700 }}>{volume.title}</div>
      <div style={{ fontFamily: "var(--kr-font-display)", fontSize: 28, fontWeight: 700, color: "#111", lineHeight: 1.15, letterSpacing: "-0.02em" }}>{lesson.title}</div>
      <div style={{ height: 2, width: 48, background: "var(--kr-action)" }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 4 }}>
        {[78, 92, 65, 88, 71, 95].map((w, i) => (
          <div key={i} style={{ height: 8, background: "#e9ecef", borderRadius: 3, width: `${w}%` }} />
        ))}
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#888", borderTop: "1px solid #eee", paddingTop: 8 }}>
        <span>Acme Industries · {volume.id.toUpperCase()}</span>
        <span style={{ fontFamily: "var(--kr-font-mono)" }}>3 / 12</span>
      </div>
    </div>
  );
}

// =====================================================================
//  Temario: volúmenes y lecciones
// =====================================================================
function ChapterSidebar({ course, currentId, onSelect }) {
  const total = totalLessons(course);
  const done = completedLessons(course);
  const progress = progressOfCourse(course);

  return (
    <Card padded={false} style={{ overflow: "hidden", maxHeight: 540, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--kr-border-subtle)" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Contenido del curso</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
          <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 18, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{done}<span style={{ color: "var(--kr-fg-muted)" }}> / {total}</span></span>
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>lecciones</span>
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, fontWeight: 700, color: "var(--kr-action-soft-fg)" }}>{Math.round(progress * 100)}%</span>
        </div>
        <div style={{ marginTop: 8 }}>
          <ProgressBar value={progress} />
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {course.volumes.map((v, vi) => (
          <VolumeBlock key={v.id} index={vi} volume={v} currentId={currentId} onSelect={onSelect} />
        ))}
      </div>
    </Card>
  );
}

function VolumeBlock({ index, volume, currentId, onSelect }) {
  const done = volume.lessons.filter((l) => l.completed).length;
  const total = volume.lessons.length;
  const totalMin = volume.lessons.reduce((a, l) => a + l.durationMin, 0);
  const [open, setOpen] = useState(true);

  return (
    <div style={{ borderBottom: "1px solid var(--kr-border-subtle)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", border: "none", background: "transparent", cursor: "pointer", padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, textAlign: "left" }}
      >
        <Icon name={open ? "ChevronDown" : "ChevronRight"} size={14} style={{ color: "var(--kr-fg-muted)" }} />
        <span style={{ flex: 1 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "var(--kr-fg-muted)", letterSpacing: "0.04em" }}>VOLUMEN {String(index + 1).padStart(2, "0")}</span>
          <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)", marginTop: 2 }}>{volume.title}</span>
        </span>
        <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)", textAlign: "right" }}>
          {done}/{total}
          <div style={{ fontSize: 10 }}>{Math.round(totalMin)} min</div>
        </span>
      </button>
      {open && (
        <div style={{ paddingBottom: 4 }}>
          {volume.lessons.map((l) => (
            <LessonRow key={l.id} lesson={l} active={l.id === currentId} onClick={() => onSelect(l.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function LessonRow({ lesson, active, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "8px 16px 8px 40px", cursor: "pointer", fontSize: 12,
        background: active ? "var(--kr-action-soft-bg)" : hover ? "var(--kr-bg-subtle)" : "transparent",
      }}
    >
      <span
        style={{
          width: 18, height: 18, borderRadius: 999, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: lesson.completed ? "var(--kr-success-500)" : active ? "var(--kr-action)" : "var(--kr-neutral-150)",
          color: "#fff",
        }}
      >
        {lesson.completed
          ? <Icon name="Check" size={10} style={{ color: "#fff" }} />
          : active
            ? <Icon name="Play" size={9} style={{ color: "#fff", marginLeft: 1 }} />
            : <Icon name={lesson.type === "video" ? "Play" : "FileText"} size={9} style={{ color: "var(--kr-fg-muted)" }} />}
      </span>
      <span style={{ flex: 1, color: active ? "var(--kr-action-soft-fg)" : "var(--kr-fg-strong)", fontWeight: active ? 600 : 400, lineHeight: 1.4 }}>{lesson.title}</span>
      <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)" }}>{lesson.duration}</span>
    </div>
  );
}

// =====================================================================
//  Contenido de las tabs
// =====================================================================
function DescripcionTab({ course }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 28 }} data-responsive-grid="2">
      <div>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 6 }}>Sobre este curso</div>
        <p style={{ margin: 0, fontSize: 13, color: "var(--kr-fg-primary)", lineHeight: 1.55, textWrap: "pretty" }}>{course.description}</p>

        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 8 }}>Lo que aprenderás</div>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {course.learn.map((it, i) => (
              <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: "var(--kr-fg-strong)", lineHeight: 1.45 }}>
                <Icon name="Check" size={14} style={{ color: "var(--kr-action)", marginTop: 2, flexShrink: 0 }} />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 8 }}>Requisitos</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: "var(--kr-fg-primary)", fontSize: 13, lineHeight: 1.55 }}>
            {course.requirements.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Card style={{ padding: 16 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Avatar name={course.instructor} size={44} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{course.instructor}</div>
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{course.instructorRole}</div>
            </div>
          </div>
          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12 }}>
            <Meta label="Valoración" value={<Stars value={course.rating} size={10} />} />
            <Meta label="Inscritos" value={`${course.students}`} mono />
            <Meta label="Volúmenes" value={`${totalVolumes(course)}`} mono />
            <Meta label="Duración" value={fmtMinutes(course.durationMin)} mono />
          </div>
        </Card>

        {course.mandatory && (
          <Card style={{ padding: 14, background: "var(--kr-status-warning-bg)", border: "1px solid var(--kr-warning-100)" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <Icon name="AlertTriangle" size={16} style={{ color: "var(--kr-warning-700)", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--kr-warning-700)" }}>Curso obligatorio</div>
                <div style={{ fontSize: 12, color: "var(--kr-warning-700)", marginTop: 4, lineHeight: 1.45 }}>
                  Asignado a toda la plantilla. Fecha límite: <strong>{course.deadline}</strong>{course.deadlineDaysLeft != null && ` · quedan ${course.deadlineDaysLeft} días`}.
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)", marginTop: 2, fontFamily: mono ? "var(--kr-font-mono)" : "var(--kr-font-sans)" }}>{value}</div>
    </div>
  );
}

// ----- Materiales -----
function MaterialesTab({ course }) {
  const items = course.volumes.flatMap((v) => v.materials.map((m) => ({ ...m, vol: v })));
  if (items.length === 0) {
    return <div style={{ padding: "32px 0", textAlign: "center", color: "var(--kr-fg-muted)", fontSize: 13 }}>Este curso aún no tiene materiales de apoyo.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
          <strong style={{ color: "var(--kr-fg-strong)" }}>{items.length} archivos</strong> · agrupados por volumen
        </div>
        <Button variant="secondary" size="sm" icon="Download">Descargar todo (.zip)</Button>
      </div>

      {course.volumes.map((v, vi) => v.materials.length > 0 && (
        <div key={v.id}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 8 }}>
            Volumen {String(vi + 1).padStart(2, "0")} · {v.title}
          </div>
          <div style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
            {v.materials.map((m, i) => (
              <MaterialRow key={m.name} mat={m} last={i === v.materials.length - 1} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MaterialRow({ mat, last }) {
  const [hover, setHover] = useState(false);
  const extColor = {
    pdf: ["var(--kr-status-danger-bg)", "var(--kr-danger-700)"],
    xlsx: ["var(--kr-status-success-bg)", "var(--kr-success-700)"],
    docx: ["var(--kr-status-info-bg)", "var(--kr-info-700)"],
    zip: ["var(--kr-status-neutral-bg)", "var(--kr-fg-strong)"],
  }[mat.ext] || ["var(--kr-neutral-100)", "var(--kr-fg-muted)"];

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "var(--kr-bg-surface)", cursor: "pointer", fontSize: 13,
      }}
    >
      <div style={{ width: 36, height: 36, borderRadius: 6, background: extColor[0], color: extColor[1], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
        {mat.ext.toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mat.name}</div>
        <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>{mat.size}</div>
      </div>
      <Button variant="ghost" size="sm" icon="Eye">Vista previa</Button>
      <Button variant="secondary" size="sm" icon="Download">Descargar</Button>
    </div>
  );
}

// ----- Foro: lista + hilo -----
function ForoList({ course, onOpen }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("todas"); // todas | abiertas | resueltas | mias

  const filtered = course.forum.filter((q) => {
    if (filter === "abiertas" && q.solved) return false;
    if (filter === "resueltas" && !q.solved) return false;
    if (filter === "mias" && q.author !== "Ana Castro") return false;
    if (query && !`${q.title} ${q.body} ${q.author}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar en el foro…" icon="Search" style={{ flex: 1, maxWidth: 320, minWidth: 200 }} />
        <ForoFilter value={filter} onChange={setFilter} />
        <div style={{ flex: 1 }} />
        <Button variant="primary" size="sm" icon="Plus">Nueva pregunta</Button>
      </div>

      {course.forum.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--kr-fg-muted)", fontSize: 13 }}>
          <Icon name="MessageSquare" size={24} style={{ color: "var(--kr-fg-subtle)", marginBottom: 8 }} />
          <div>Aún no hay preguntas en este curso.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Sé el primero en abrir un hilo.</div>
        </div>
      ) : (
        <div style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
          {filtered.map((q, i) => (
            <ThreadRow key={q.id} thread={q} course={course} last={i === filtered.length - 1} onClick={() => onOpen(q.id)} />
          ))}
          {filtered.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "var(--kr-fg-muted)", fontSize: 12 }}>Ningún hilo coincide con tu filtro.</div>}
        </div>
      )}
    </div>
  );
}

function ForoFilter({ value, onChange }) {
  const opts = [
    { id: "todas", label: "Todas" },
    { id: "abiertas", label: "Abiertas" },
    { id: "resueltas", label: "Resueltas" },
    { id: "mias", label: "Mis hilos" },
  ];
  return (
    <div style={{ display: "inline-flex", background: "var(--kr-neutral-100)", borderRadius: 6, padding: 2 }}>
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          style={{
            border: "none", background: value === o.id ? "var(--kr-bg-surface)" : "transparent",
            boxShadow: value === o.id ? "var(--kr-shadow-xs)" : "none",
            color: value === o.id ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
            fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 4, cursor: "pointer",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ThreadRow({ thread, course, last, onClick }) {
  const [hover, setHover] = useState(false);
  const vol = course.volumes.find((v) => v.id === thread.volume);
  const volIdx = course.volumes.findIndex((v) => v.id === thread.volume);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 14, alignItems: "center",
        padding: "14px 16px", borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "var(--kr-bg-surface)", cursor: "pointer",
      }}
    >
      <Avatar name={thread.author} size={36} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{thread.title}</span>
          {thread.solved && <Badge tone="success" dot>Resuelto</Badge>}
        </div>
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4, lineHeight: 1.45, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 720 }}>{thread.body}</div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span><strong style={{ color: "var(--kr-fg-strong)" }}>{thread.author}</strong> · {thread.when}</span>
          <span>·</span>
          <span>Vol. {String(volIdx + 1).padStart(2, "0")} · {vol?.title}</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 600 }}>
          <Icon name="MessageSquare" size={12} style={{ color: "var(--kr-fg-muted)" }} />
          {thread.replies}
        </span>
        <span>Última: {thread.lastReply}</span>
      </div>
    </div>
  );
}

function ForumThread({ course, thread, onBack }) {
  const vol = course.volumes.find((v) => v.id === thread.volume);
  const volIdx = course.volumes.findIndex((v) => v.id === thread.volume);
  const replies = useMemo(() => buildReplies(thread, course), [thread, course]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <button
        type="button"
        onClick={onBack}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "var(--kr-fg-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, alignSelf: "flex-start" }}
      >
        <Icon name="ArrowLeft" size={14} />
        Volver al foro
      </button>

      {/* Pregunta */}
      <div style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 8, padding: 16, background: "var(--kr-bg-surface)" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <Avatar name={thread.author} size={40} />
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
                <strong style={{ color: "var(--kr-fg-strong)" }}>{thread.author}</strong> preguntó · {thread.when}
              </span>
              <Badge tone="neutral">Vol. {String(volIdx + 1).padStart(2, "0")} · {vol?.title}</Badge>
              {thread.solved && <Badge tone="success" dot>Resuelto</Badge>}
            </div>
            <h3 style={{ margin: "8px 0 6px", fontSize: 18, fontWeight: 700, color: "var(--kr-fg-strong)", lineHeight: 1.25, letterSpacing: "-0.01em" }}>{thread.title}</h3>
            <p style={{ margin: 0, fontSize: 14, color: "var(--kr-fg-primary)", lineHeight: 1.55, textWrap: "pretty" }}>{thread.body}</p>

            <div style={{ display: "flex", gap: 4, marginTop: 12 }}>
              <Button variant="ghost" size="sm" icon="ThumbsUp">6</Button>
              <Button variant="ghost" size="sm" icon="Bookmark">Guardar</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Cabecera de respuestas */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
          {replies.length} {replies.length === 1 ? "respuesta" : "respuestas"}
        </div>
        <div style={{ display: "inline-flex", background: "var(--kr-neutral-100)", borderRadius: 6, padding: 2 }}>
          <button type="button" style={{ border: "none", background: "var(--kr-bg-surface)", boxShadow: "var(--kr-shadow-xs)", padding: "4px 10px", fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: "pointer", color: "var(--kr-fg-strong)" }}>Más útiles</button>
          <button type="button" style={{ border: "none", background: "transparent", padding: "4px 10px", fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: "pointer", color: "var(--kr-fg-muted)" }}>Recientes</button>
        </div>
      </div>

      {/* Respuestas */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {replies.map((r) => <ReplyCard key={r.id} reply={r} />)}
      </div>

      {/* Composer */}
      <div style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 8, padding: 14, background: "var(--kr-neutral-50)", marginTop: 4 }}>
        <div style={{ display: "flex", gap: 10 }}>
          <Avatar name="Ana Castro" size={32} />
          <div style={{ flex: 1 }}>
            <textarea
              placeholder="Escribe tu respuesta…"
              style={{ width: "100%", minHeight: 80, border: "1px solid var(--kr-border)", background: "var(--kr-bg-surface)", borderRadius: 6, padding: 10, fontSize: 13, fontFamily: "var(--kr-font-sans)", outline: "none", resize: "vertical", color: "var(--kr-fg-strong)", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
              <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>Tu respuesta es visible para todos los inscritos al curso.</span>
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="ghost" size="sm">Cancelar</Button>
                <Button variant="primary" size="sm" icon="Send">Publicar respuesta</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReplyCard({ reply }) {
  return (
    <div
      style={{
        border: reply.accepted ? "1px solid var(--kr-success-500)" : "1px solid var(--kr-border-subtle)",
        borderRadius: 8, padding: 14, background: reply.accepted ? "var(--kr-status-success-bg)" : "var(--kr-bg-surface)",
      }}
    >
      <div style={{ display: "flex", gap: 12 }}>
        <Avatar name={reply.author} size={32} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{reply.author}</span>
            {reply.role && <Badge tone={reply.role === "Formador" ? "brand" : "neutral"}>{reply.role}</Badge>}
            <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>· {reply.when}</span>
            {reply.accepted && <Badge tone="success" dot>Respuesta aceptada</Badge>}
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--kr-fg-primary)", lineHeight: 1.55, textWrap: "pretty" }}>{reply.body}</p>
          <div style={{ display: "flex", gap: 4, marginTop: 10 }}>
            <Button variant="ghost" size="sm" icon="ThumbsUp">{reply.votes}</Button>
            <Button variant="ghost" size="sm">Responder</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function buildReplies(thread, course) {
  const base = [
    { id: "r1", author: course.instructor, role: "Formador", when: "Hace 1 d", body: replyBody(thread, 0), accepted: thread.solved, votes: 6 },
    { id: "r2", author: "Marta Iglesias", role: "RRHH", when: "Hace 18 h", body: replyBody(thread, 1), accepted: false, votes: 3 },
  ];
  if (thread.replies > 2) {
    base.push({ id: "r3", author: "Pablo Ruiz", role: null, when: thread.lastReply, body: replyBody(thread, 2), accepted: false, votes: 1 });
  }
  if (thread.replies > 3) {
    base.push({ id: "r4", author: "Lucía Romero", role: null, when: thread.lastReply, body: "Comparto la duda — sería útil que el material quede enlazado desde la lección misma, no solo en la pestaña.", accepted: false, votes: 2 });
  }
  return base.slice(0, thread.replies);
}

function replyBody(thread, idx) {
  const POOL = [
    "Gracias por la pregunta. Lo verás cubierto en la siguiente lección, pero te dejo aquí el resumen: depende del caso concreto, así que abre un ticket interno si necesitas confirmación oficial.",
    "Buena observación. He añadido el material que faltaba al volumen, debería aparecerte en cuanto refresques la pestaña de Materiales.",
    "La política actual es la que se aplica desde enero de 2026, no la anterior. Si te quedan dudas concretas sobre tu caso, ping a RRHH.",
    "Tienes razón, lo apunto para la próxima revisión del curso. Mientras tanto, sigue el procedimiento de la lección anterior.",
    "Exactamente. Lo importante es documentar la decisión: queda registrada en el sistema y luego se puede auditar sin problema.",
  ];
  return POOL[(thread.id.length + idx) % POOL.length];
}

export default FormacionCurso;
